/**
 * PET-EXP-26-CARE-PLAN-QUESTIONS — 병원 ↔ 집 왕복.
 *
 * 늘곁에는 매일 기록과 재진 리포트를 이미 소유한다. 이 모듈은 그 사이에 빠져 있던
 * "병원에서 받은 계획을 집에서 실행하고, 그 사이 생긴 질문을 다음 방문에 가져간다"를 닫는다.
 *
 * 계약 셋:
 * 1. 모든 값은 **보호자가 입력한 사실**이다. 의학 판단·용량 계산·진단을 만들지 않는다.
 * 2. 계획은 만료되며, 만료를 조용히 넘기지 않고 재확인 카드로 되돌린다.
 * 3. 답을 못 받은 질문은 사라지지 않고 **다음 방문으로 이월**된다.
 */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;
function assertDate(value, field) {
    if (!DATE_PATTERN.test(value))
        throw new RangeError(`${field} must be YYYY-MM-DD`);
    return value;
}
function dayDifference(from, to) {
    const start = Date.parse(`${from}T00:00:00Z`);
    const end = Date.parse(`${to}T00:00:00Z`);
    return Math.round((end - start) / 86_400_000);
}
function planState(plan, today) {
    if (plan.validUntil && plan.validUntil < today)
        return 'expired';
    if (plan.recheckOn && plan.recheckOn <= today)
        return 'recheck_due';
    if (!plan.validUntil && !plan.recheckOn)
        return 'open_ended';
    return 'active';
}
/**
 * 방문 준비 화면의 단일 계산기. 계획·질문·사건 묶음을 한 번에 판정한다.
 * `visitDates`는 보호자가 기록한 과거 방문일이며 질문의 이월 횟수를 세는 데만 쓴다.
 */
export function buildVisitPreparation(input) {
    const today = assertDate(input.today, 'today');
    const visitDates = [...input.visitDates].map((date) => assertDate(date, 'visitDate')).sort();
    const plans = [...input.plans]
        .map((plan) => {
        assertDate(plan.source.checkedOn, 'source.checkedOn');
        if (plan.validUntil)
            assertDate(plan.validUntil, 'validUntil');
        if (plan.recheckOn)
            assertDate(plan.recheckOn, 'recheckOn');
        return {
            ...plan,
            state: planState(plan, today),
            daysUntilRecheck: plan.recheckOn ? dayDifference(today, plan.recheckOn) : null,
        };
    })
        // 주의가 필요한 계획을 먼저 보여준다.
        .sort((left, right) => {
        const weight = (state) => state === 'expired' ? 0 : state === 'recheck_due' ? 1 : state === 'active' ? 2 : 3;
        return weight(left.state) - weight(right.state)
            || left.source.checkedOn.localeCompare(right.source.checkedOn)
            || left.id.localeCompare(right.id);
    });
    const questions = [...input.questions]
        .map((question) => {
        assertDate(question.askedOn, 'askedOn');
        if (question.answeredOn)
            assertDate(question.answeredOn, 'answeredOn');
        const answered = question.answeredOn !== null;
        // 질문이 올라온 뒤부터 (답을 받았다면 그 전까지) 지나간 방문 수.
        const boundary = question.answeredOn ?? today;
        const carriedVisitCount = visitDates.filter((date) => date > question.askedOn && date <= boundary).length;
        return { ...question, state: answered ? 'answered' : 'open', carriedVisitCount };
    })
        .sort((left, right) => left.askedOn.localeCompare(right.askedOn) || left.id.localeCompare(right.id));
    const eventGroups = [...input.eventGroups]
        .filter((group) => group.dates.length > 0)
        .map((group) => {
        const dates = [...group.dates].map((date) => assertDate(date, 'eventGroup.date')).sort();
        const startDate = dates[0];
        const endDate = dates.at(-1);
        return {
            ...group,
            startDate,
            endDate,
            spanDays: dayDifference(startDate, endDate) + 1,
            recordCount: new Set(group.recordIds).size,
        };
    })
        .sort((left, right) => right.startDate.localeCompare(left.startDate) || left.id.localeCompare(right.id));
    const openQuestions = questions.filter((question) => question.state === 'open');
    const planAttentionCount = plans.filter((plan) => plan.state === 'expired' || plan.state === 'recheck_due').length;
    return {
        plans,
        openQuestions,
        answeredQuestions: questions.filter((question) => question.state === 'answered'),
        eventGroups,
        planAttentionCount,
        hasSomethingToBring: plans.length > 0 || openQuestions.length > 0 || eventGroups.length > 0,
    };
}
/**
 * 방문을 닫는다. 답을 받은 질문만 답변으로 기록하고, **나머지는 열린 채로 남긴다.**
 * 방문했다는 이유로 질문을 자동으로 닫지 않는다.
 */
export function closeVisit(input) {
    const visitedOn = assertDate(input.visitedOn, 'visitedOn');
    return input.questions.map((question) => {
        if (question.answeredOn !== null)
            return question;
        const answer = input.answers.get(question.id)?.normalize('NFC').trim();
        if (!answer)
            return question;
        return { ...question, answeredOn: visitedOn, answer };
    });
}
